import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { signalsApi, outreachApi } from "@/lib/api";
import { applyJourneyToAbmLead } from "@/lib/abmLead";
import { RncShell, FlowCanvas } from "@/pages/signals/rnc/RncShared";
import { signalsDashboardPath, SIGNALS_TAB_ABM } from "@/pages/signals/signalsNav";
import PropTypes from "prop-types";
import "@/pages/signals/rnc/rncPlatform.css";

function AbmCrumb({ company }) {
  return (
    <>
      <Link to={signalsDashboardPath()} className="link" style={{ color: "inherit", textDecoration: "none" }}>Signals</Link>
      <span className="sep">/</span>
      <Link to={signalsDashboardPath(SIGNALS_TAB_ABM)} className="link" style={{ color: "inherit", textDecoration: "none" }}>ABM List</Link>
      <span className="sep">/</span>
      <b>{company}</b>
    </>
  );
}

AbmCrumb.propTypes = {
  company: PropTypes.string.isRequired,
};

export default function AbmWorkflowViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, lead: null, journey: null, error: null });

  useEffect(() => {
    let cancelled = false;
    const leadId = decodeURIComponent(id || "").trim();
    setState({ loading: true, lead: null, journey: null, error: null });
    Promise.all([
      signalsApi.getCustomLead(leadId),
      outreachApi.getAbmLeadJourney(leadId).catch(() => null),
    ])
      .then(([data, journey]) => {
        if (!cancelled) {
          setState({
            loading: false,
            lead: data?.lead || null,
            journey,
            error: null,
          });
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setState({
            loading: false,
            lead: null,
            journey: null,
            error: e.message || "Failed to load workflow",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const lead = useMemo(() => {
    if (!state.lead) return null;
    return applyJourneyToAbmLead(state.lead, state.journey?.journey || state.journey);
  }, [state.lead, state.journey]);

  if (state.loading) {
    return (
      <RncShell crumb={<AbmCrumb company="Loading…" />}>
        <div className="card" style={{ padding: 20 }} data-testid="abm-workflow-loading">
          <Skeleton className="h-8 w-1/2 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </RncShell>
    );
  }

  if (state.error || !lead) {
    return (
      <RncShell crumb={<AbmCrumb company="Lead" />}>
        <div className="card" style={{ padding: 20 }} data-testid="abm-workflow-error">
          <p style={{ color: "#b91c1c", margin: 0 }}>{state.error || "Lead not found"}</p>
          <p style={{ marginTop: 12 }}>
            <Link to={signalsDashboardPath(SIGNALS_TAB_ABM)} style={{ color: "var(--indigo)" }}>Return to ABM List</Link>
          </p>
        </div>
      </RncShell>
    );
  }

  const assigned = Boolean(lead.workflowAssigned);
  const wf = lead.signalWorkflow;

  return (
    <RncShell
      crumb={(
        <>
          <AbmCrumb company={lead.company} />
          <span className="sep">/</span>
          <b>Workflow Viewer</b>
        </>
      )}
    >
      <div className="page-head">
        <div>
          <h1>
            {assigned && (wf?.name || lead.campaignName) ? (wf?.name || lead.campaignName) : "No Workflow Assigned"}
          </h1>
          <p>
            {assigned
              ? (
                <>
                  Read-only view of where <b>{lead.company}</b> ({lead.id}) currently sits inside this workflow.
                </>
              )
              : (
                <>
                  <b>{lead.company}</b> ({lead.id}) is pending workflow assignment. Auto-enroll starts when a channel default is set.
                </>
              )}
          </p>
        </div>
        <button
          type="button"
          className="btn whitespace-nowrap"
          onClick={() => navigate(`/signals/abm/${encodeURIComponent(lead.id)}`)}
          data-testid="back-to-abm"
        >
          <ArrowLeft size={14} /> Back to ABM Details
        </button>
      </div>
      <div className="legend">
        <span><span className="sw" style={{ background: "var(--green-600)" }} />Completed</span>
        <span><span className="sw" style={{ background: "var(--blue-500)" }} />Current step</span>
        <span><span className="sw" style={{ background: "var(--slate-300, #CBD5E1)" }} />Upcoming</span>
        <span style={{ marginLeft: "auto", color: "var(--slate-400)" }}>
          Campaign: {lead.campaignName || "N/A"}
        </span>
      </div>
      <div className="builder-shell" style={{ gridTemplateColumns: "1fr" }}>
        <div className="canvas" style={{ padding: 34 }}>
          <FlowCanvas workflow={wf} progressIndex={assigned ? lead.stepIndex : null} />
        </div>
      </div>
    </RncShell>
  );
}
